import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { isArray, reduce, filter, find, concat } from 'lodash';
import moment from 'moment';

import GodTable, { CUSTOM_FIELD_TYPES } from '../../../../components/god-table';

// Import Selectors
import { getTablePreference } from '../../../App/AppReducer';
import ArrayRenderer from '../../../../components/table-cells/ArrayRenderer';
import OpportunityNameRenderer from '../../../../components/table-cells/OpportunityNameRenderer';
import AccountNameRenderer from '../../../../components/table-cells/AccountNameRenderer';
import SaleRepsNameRenderer from '../../../../components/table-cells/SaleRepsNameRenderer';
import AgeRenderer from '../../../../components/table-cells/AgeRenderer';
import LogRenderer from '../../../../components/table-cells/LogRenderer';
import {
  dateComparator, minDateAggregation, stringComparator,
} from '../../../../components/table-cells/helpers';

import styles from './styles.css';

const NULL_COLUMN = 'null';

class OpportunityTable extends Component {
  constructor(props) {
    super(props);
    this.state = {
      groupingByCloseDate: false,
      closeDateGroupingType: 'monthly',
      dealType: null,
    };
    this.hanldeOnColumnRowGroupChanged = this.hanldeOnColumnRowGroupChanged.bind(this);
    this.onGridReady = this.onGridReady.bind(this);
    this.getMainMenuItems = this.getMainMenuItems.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.dealType !== this.props.dealType) {
      this.setState({ dealType: nextProps.dealType });
    }
  }

  onGridReady(params) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    params.api.addAggFunc('minDate', minDateAggregation);
    this.props.onGridReady(params);
  }

  getDealTypes() {
    const { dealType } = this.state;
    const { allDealTypes } = this.props;
    const totalMenu = { label: 'All Opportunities', value: null };
    const active = find(allDealTypes, { value: dealType }) || totalMenu;
    return {
      name: `Showing: ${active.label}`,
      checked: active !== undefined,
      subMenu: allDealTypes.length ? concat([totalMenu, 'separator'], allDealTypes).map((stat) => {
        if (stat !== 'separator') {
          return {
            name: stat.label,
            checked: stat.value === dealType,
            action: () => { this.setState({ dealType: stat.value }); },
          };
        }
        return stat;
      }) : null,
    };
  }

  getMainMenuItems(params) {
    const { groupingByCloseDate, closeDateGroupingType } = this.state;
    const columnId = params.column.getId();
    const { cellRendererParams } = params.column.colDef;

    if (cellRendererParams&& groupingByCloseDate && cellRendererParams.column && cellRendererParams.column.tag === 'closeDate') {
      const menuItems = params.defaultItems.slice(0);
      const subMenus = [
        { name: 'Monthly', value: 'monthly' },
        { name: 'Quarterly', value: 'quarterly' },
        { name: 'Yearly', value: 'yearly' },
      ];
      menuItems.push({
        name: 'Group By Close Date',
        checked: groupingByCloseDate,
        subMenu: subMenus.map(item => ({
          ...item,
          checked: item.value === closeDateGroupingType,
          action: () => {
            this.setState(
              { closeDateGroupingType: item.value },
              () => {
                if (this.gridApi) {
                  this.gridApi.refreshClientSideRowModel('group');
                }
              },
            );
          },
        })),
      });
      return menuItems;
    }
    if (cellRendererParams && cellRendererParams.column && cellRendererParams.column.tag === 'opportunityType') {
      const menuItems = params.defaultItems.slice(0);
      menuItems.unshift('separator');
      menuItems.unshift(this.getDealTypes());
      return menuItems;
    }
    return params.defaultItems;
  }


  getCloseDateColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'close_date',
      sort: 'desc',
      type: 'dateColumn',
      cellRendererSelector: (params) => {
        if (!params) {
          return null;
        }
        if (this.state.groupingByCloseDate) {
          return null;
        }
        return {
          component: LogRenderer,
          params: {
            field: 'close_date',
          },
        };
      },
      enableRowGroup: true,
      cellRendererParams: {
        isCloseDate: true,
        logField: 'close_date',
        column,
      },
      valueGetter: (params) => {
        if (params && params.data) {
          return moment(params.data.close_date).format('YYYY-MM-DD');
        }
        return null;
      },
      keyCreator: (params) => {
        const vals = params.data?.close_date;
        if (!vals) {
          return '--';
        }
        let format = 'Y \\QQ';
        if (this.state.closeDateGroupingType === 'quarterly') {
          format = 'Y \\QQ';
        } else if (this.state.closeDateGroupingType === 'monthly') {
          format = 'Y MM';
        } else if (this.state.closeDateGroupingType === 'yearly') {
          format = 'Y';
        }
        return moment(vals).format(format);
      },
    };
  }

  getNameColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'name',
      cellClass: column.auto_height ? 'cell-wrap-text' : null,
      autoHeight: column.auto_height,
      comparator: stringComparator,
      cellRenderer: OpportunityNameRenderer,
      cellRendererParams: {
        enableAutoHeight: true,
        column,
        exportURLType: 'opportunity',
      },
    };
  }

  getAccountColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      enablePivot: true,
      enableRowGroup: true,
      cellClass: column.auto_height ? 'cell-wrap-text' : null,
      autoHeight: column.auto_height,
      // field: 'account.name',
      valueGetter: (params) => {
        if (!params || !params.data || params.node.group) {
          return null;
        }
        const { data } = params;
        return data.account?.name || '--';
      },
      cellRendererSelector: (params) => {
        if (!params) {
          return null;
        }
        return {
          component: AccountNameRenderer,
          params: {
            exportURLType: 'account',
          }
        };
      },
      cellRendererParams: {
        enableAutoHeight: true,
        column,
      },
    };
  }

  getAmountColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'amount',
      type: 'numericColumn',
      aggFunc: 'sum',
      cellClass: 'text-left', // PI asked for this
      enableRowGroup: true,
      enablePivot: true,
      cellRenderer: LogRenderer,
      cellRendererParams: {
        field: 'amount',
        column,
      },
      enableValue: true,
      // allowedAggFuncs: ['sum', 'min', 'max', 'avg'],
    };
  }

  getStageColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      enablePivot: true,
      enableRowGroup: true,
      cellClass: column.auto_height ? 'cell-wrap-text' : null,
      autoHeight: column.auto_height,
      valueGetter: (params) => {
        if (!params || !params.data || params.node.group) {
          return null;
        }
        const { data } = params;
        return data.is_closed ? data.stage : data.stage_name;
      },
      cellRendererSelector: (params) => {
        if (!params || !params.data) {
          return null;
        }
        return {
          component: LogRenderer,
          params: {
            field: params.data.is_closed ? 'stage' : 'stage_name',
            logField: 'stage_name',
          },
        };
      },
    };
  }

  getProductColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'products',
      //cellRenderer: 'arrayRenderer',
      cellClass: column.auto_height ? 'cell-wrap-text' : null,
      autoHeight: column.auto_height,
      wrapText: column.auto_height,
      cellRendererParams: {
        enableAutoHeight: true,
        column,
      },
    };
  }

  getAgeColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'pursuit_length',
      cellRenderer: AgeRenderer,
      type: 'numericColumn',
      enableValue: true,
      allowedAggFuncs: ['sum', 'min', 'max', 'avg'],
      cellRendererParams: {
        column,
      },
    };
  }

  getOpportunityTypeColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'types',
      enableRowGroup: true,
      filter: false, // we use our own filter
      menuTabs: ['generalMenuTab', 'columnsMenuTab'], // we use our own filter
      valueGetter: (params) => {
        if (!params || !params.data || params.node.group) {
          return null;
        }
        const { data } = params;
        const types = data.types || [];

        const allTypes = isArray(types) ? types : [types];
        return allTypes.join(', ');
      },
      cellRendererParams: {
        column,
      },
    };
  }

  getSalesRepColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'owner.name',
      enableRowGroup: true,
      enablePivot: true,
      cellRenderer: SaleRepsNameRenderer,
      cellRendererParams: {
        column,
        exportURLType: 'owner',
      },
    };
  }

  getCategoryColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'forecast_category_name',
      enableRowGroup: true,
      enablePivot: true,
      cellRenderer: LogRenderer,
      cellRendererParams: {
        column,
        field: 'forecast_category_name',
        logField: 'forecast_category_name',
      },
    };
  }


  getContactCountColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'contact_count',
      type: 'numericColumn',
      enableValue: true,
      allowedAggFuncs: ['sum', 'min', 'max', 'avg'],
      cellRendererParams: {
        column,
      },
    };
  }

  getSalesRegionColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'rep_regions',
      cellRenderer: ArrayRenderer,
      cellRendererParams: {
        column,
      },
    };
  }

  getSalesTypeColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'rep_types',
      cellRenderer: ArrayRenderer,
      cellRendererParams: {
        column,
      },
    };
  }

  getRequestedFeedbackColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: column.target_field,
      cellRenderer: 'requestedFeedbackRenderer',
      cellRendererParams: {
        column,
      },
    };
  }

  getColDefs() {
    const { tableColumns } = this.props;
    return reduce(tableColumns, (columns, tableColumn) => {
      let column = null;

      const externalColumn = this.props.getColDefs ? this.props.getColDefs(tableColumn) : null;

      if (externalColumn === NULL_COLUMN) {
        column = null;
      } else if (externalColumn) {
        column = externalColumn;
      } else if (tableColumn.tag === 'salesRep') {
        column = this.getSalesRepColumn(tableColumn);
      } else if (tableColumn.tag === 'opportunity') {
        column = this.getNameColumn(tableColumn);
      } else if (tableColumn.tag === 'account') {
        column = this.getAccountColumn(tableColumn);
      } else if (tableColumn.tag === 'opportunityAmount') {
        column = this.getAmountColumn(tableColumn);
      } else if (tableColumn.tag === 'opportunityType') {
        column = this.getOpportunityTypeColumn(tableColumn);
      } else if (tableColumn.tag === 'closeDate') {
        column = this.getCloseDateColumn(tableColumn);
      } else if (tableColumn.tag === 'stage') {
        column = this.getStageColumn(tableColumn);
      } else if (tableColumn.tag === 'age') {
        column = this.getAgeColumn(tableColumn);
      } else if (tableColumn.tag === 'category') {
        column = this.getCategoryColumn(tableColumn);
      } else if (tableColumn.tag === 'salesRegion') {
        column = this.getSalesRegionColumn(tableColumn);
      } else if (tableColumn.tag === 'salesType') {
        column = this.getSalesTypeColumn(tableColumn);
      } else if (tableColumn.tag === 'contactCount') {
        column = this.getContactCountColumn(tableColumn);
      } else if (tableColumn.tag === 'products') {
        column = this.getProductColumn(tableColumn);
      } else if (tableColumn.tag === 'opportunityDetails') {
        column = null; // old table version
      } else if (tableColumn.tag === 'salesDetails') {
        column = null; // old tableversion
      } else if (tableColumn.tag === 'opportunity') {
        column = null; // old tableversion
      } else if (tableColumn.tag === 'stages') {
        column = null; // old tableversion
      } else if (tableColumn.target_object === 'Opportunity' && tableColumn.target_field === 'requested_feedbacks') {
        column = this.getRequestedFeedbackColumn(tableColumn);
      } else {
        column = {
          column: tableColumn,
          customFieldType: CUSTOM_FIELD_TYPES.OPPORTUNITY,
        };
      }
      if (column) {
        columns.push(column);
      }
      return columns;
    }, []);
  }

  hanldeOnColumnRowGroupChanged(event) {
    const { columns } = event;
    const closeDateColumn = find(columns, col => col.colDef && col.colDef.cellRendererParams && col.colDef.cellRendererParams.isCloseDate);
    
    this.setState({ groupingByCloseDate: closeDateColumn !== undefined });
  }

  renderTable(items) {
    const columnDefs = [
      ...this.getColDefs(),
      ...this.props.columns,
    ];
    return (
      <GodTable
        id={this.props.tableName}
        columnDefs={columnDefs}
        rowData={items}
        reactNext
        onGridReady={this.onGridReady}
        animateRows
        getRowNodeId={d => d.opportunity_id}
        columnTypes={{
          dateColumn: {
            filter: 'agDateColumnFilter',
            filterParams: {
              comparator: dateComparator,
            },
          },
        }}
        leftControls={this.props.leftControls}
        onColumnRowGroupChanged={this.hanldeOnColumnRowGroupChanged}
        getMainMenuItems={this.getMainMenuItems}
        enableFullScreen={this.props.enableFullScreen}
        fullScreenMode={this.props.fullScreenMode}
        onExitFullScreen={this.props.onExitFullScreen}
        onEnterFullScreen={this.props.onEnterFullScreen}
        fullScreenOffset={this.props.fullScreenOffset}
        tableManageConfigs={{
          enableCustomMeta: true,
          validCustomMetaObjects: ['Opportunity'],
        }}
        height={this.props.height}
        getRowClass={this.props.getRowClass}
        resourceType={GodTable.RESOURCE_TYPE.OPPORTUNITY}
      />
    );
  }

  render() {
    const {
      opportunities,
      title, filterOpportunities,
    } = this.props;
    const displayTitle = title !== null && title.length > 0;
    const { dealType } = this.state;
    let filteredOpportunities = opportunities;
    const isFiltering = dealType !== null;
    filteredOpportunities = filter(filteredOpportunities, (o) => {
      let oTypes = o.types || [];
      if (oTypes.length === 0) {
        oTypes = ['Others'];
      }
      const matchedTypes = isFiltering ? (filter(oTypes, t => t === dealType)).length > 0 : true;
      return matchedTypes;
    });

    if (filterOpportunities) {
      filteredOpportunities = filterOpportunities(filteredOpportunities);
    }

    return (
      <div>
        {displayTitle && <h4 className="page-sub-header">{title}</h4>}
        <div className={styles.wrapper}>
          {opportunities.length > 0 && this.renderTable(filteredOpportunities)}
        </div>
      </div>
    );
  }
}

// Retrieve data from store as props
function mapStateToProps(state, ownProps) {
  return {
    tableColumns: getTablePreference(state, ownProps.tableName),
  };
}

OpportunityTable.propTypes = {
  opportunities: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  dealType: PropTypes.string,
  allDealTypes: PropTypes.arrayOf(PropTypes.shape({})),
  tableName: PropTypes.string.isRequired,
  title: PropTypes.string,
  tableColumns: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string.isRequired,
    description: PropTypes.string,
    is_system: PropTypes.bool,
    is_visible: PropTypes.bool,
    position: PropTypes.number,
    width: PropTypes.number,
    tag: PropTypes.string,
    label: PropTypes.string,
    target_object: PropTypes.string,
    target_field: PropTypes.string,
  })),
  fullScreenMode: PropTypes.bool,
  onExitFullScreen: PropTypes.func,
  filterOpportunities: PropTypes.func,
  onEnterFullScreen: PropTypes.func,
  fullScreenOffset: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }),
  onGridReady: PropTypes.func,
  getRowClass: PropTypes.func,
  columns: PropTypes.arrayOf(PropTypes.shape({})),
  frameworkComponents: PropTypes.shape({}),
  height: PropTypes.number,
  enableFullScreen: PropTypes.bool,
  getColDefs: PropTypes.func,
  leftControls: PropTypes.node,
};

OpportunityTable.defaultProps = {
  dealType: null,
  tableColumns: [],
  leftControls: null,
  filterOpportunities: null,
  allDealTypes: [
    { label: 'Renewal', value: 'Renewal' },
    { label: 'New business', value: 'New business' },
    { label: 'Add-on', value: 'Add-on' },
    { label: 'Upsell', value: 'Upsell' },
  ],
  title: null,
  fullScreenMode: false,
  onExitFullScreen: () => {},
  onEnterFullScreen: () => {},
  onGridReady: () => {},
  fullScreenOffset: {
    x: 0,
    y: 0,
  },
  getRowClass: null,
  columns: [],
  frameworkComponents: {},
  height: null,
  enableFullScreen: true,
  getColDefs: () => { return null },
};

const COMP = connect(mapStateToProps)(OpportunityTable);

COMP.NULL_COLUMN = NULL_COLUMN;

export default COMP;